import React from 'react';
import { FileText, StickyNote } from 'lucide-react';
import type { ContextItem } from '@Types/knowledge';
import {
  contextAvailabilityLabel,
  getIngestionStatus,
  sourceFileKind,
} from '@/utils/knowledgeStatus';

interface KnowledgeSourceTileProps {
  item: ContextItem;
  memoryNames?: string[];
  readOnly?: boolean;
  onClick: () => void;
}

export const KnowledgeSourceTile = ({
  item,
  memoryNames = [],
  onClick,
}: KnowledgeSourceTileProps) => {
  const isNote = item.item_type === 'note';
  const ingestion = getIngestionStatus(item);
  const availability = contextAvailabilityLabel(item);

  const metaParts: string[] = [];
  if (isNote) metaParts.push('note');
  else metaParts.push(sourceFileKind(item.source_filename));
  if (item.chunk_count) {
    metaParts.push(
      `${item.chunk_count} passage${item.chunk_count === 1 ? '' : 's'}`,
    );
  }

  const accent = isNote ? 'border-l-[#C4A35A]' : 'border-l-[#5C7A66]';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left rounded-lg border border-[#EFF3F5] border-l-[3px] ${accent} bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,.02)] hover:border-[#DDE4E8] hover:shadow-[0_6px_18px_rgba(16,24,40,.07)] hover:-translate-y-px transition-all`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="h-8 w-8 rounded-lg border border-[#E4E9EC] bg-[#FAFBFC] text-[#41525A] flex items-center justify-center group-hover:bg-white transition-colors">
          {isNote ? (
            <StickyNote className="h-4 w-4" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
        </div>
        <span
          className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${
            ingestion === 'processing'
              ? 'text-[#7A5B14] bg-[#FFF4D6]'
              : ingestion === 'failed'
              ? 'text-[#8B2E2E] bg-[#FCEAEA]'
              : 'text-[#4F6B58] bg-[#EEF4EF]'
          }`}>
          {availability}
        </span>
      </div>
      <div className="text-[13.5px] font-semibold text-[#252F33] leading-snug line-clamp-2">
        {item.name}
      </div>
      <div className="text-xs text-[#768D95] mt-1.5">
        {metaParts.join(' · ')}
      </div>
      {memoryNames.length > 0 && (
        <div className="text-[11.5px] text-[#768D95] mt-2 flex items-center gap-1">
          <StickyNote className="h-3 w-3" />
          used in {memoryNames.join(', ')}
        </div>
      )}
      {item.scope === 'tenant' && (
        <div className="text-[11px] text-[#768D95] mt-1">Shared with team</div>
      )}
    </button>
  );
};
